import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, Headphones, Mic } from "lucide-react";
import { api, BRAND_IMAGE, YOUTUBE_CHANNEL } from "../../lib/api";
import { scrollToId } from "../../lib/lenis";

const lineAnim = (i) => ({
  initial: { y: "115%" },
  animate: { y: "0%" },
  transition: { duration: 1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
});

export const WaveBars = ({ className = "", color = "bg-gold-bright" }) => (
  <div className={`flex items-end gap-[3px] h-5 ${className}`} aria-hidden="true">
    {[0.9, 0.4, 1.2, 0.6, 1.4, 0.5, 1.0, 0.7, 1.1].map((d, i) => (
      <span
        key={i}
        className={`eq-bar w-[3px] rounded-full h-full ${color}`}
        style={{ animationDelay: `${i * 0.09}s`, animationDuration: `${0.7 + d * 0.35}s` }}
      />
    ))}
  </div>
);

export default function Hero() {
  const ref = useRef(null);
  const [cfg, setCfg] = useState(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    api.getConfig().then(setCfg).catch(() => {});
  }, []);

  const eyebrow = cfg?.hero_eyebrow || "WHERE CULTURE MEETS MEDIA";
  const subtitle =
    cfg?.hero_subtitle ||
    "WELCOME TO CULTURE'S BRIDGE.\nTap in. The conversation is always happening.";

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex flex-col overflow-hidden" data-testid="hero-section">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 80% 60% at 70% 20%, rgba(200,90,23,0.14) 0%, transparent 55%), radial-gradient(ellipse 60% 50% at 15% 85%, rgba(194,141,88,0.08) 0%, transparent 60%)" }}
      />
      <div className="flex-1 flex items-center max-w-[1400px] w-full mx-auto px-5 md:px-10 pt-32 pb-16 relative">
        <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 w-full lg:w-[62%]">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-tan border border-tan/30 rounded-full px-4 py-2" data-testid="hero-eyebrow">
              <Mic size={12} className="text-burnt" /> {eyebrow}
            </span>
            <WaveBars className="hidden sm:flex" />
          </motion.div>

          <h1 className="font-display leading-[0.82] tracking-tight select-none" data-testid="hero-title">
            <span className="block overflow-hidden pb-1">
              <motion.span {...lineAnim(0)} className="block text-outline-cream text-[clamp(2.2rem,6vw,4.5rem)] tracking-[0.3em]">
                THE
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span {...lineAnim(1)} className="block text-cream text-[clamp(4.5rem,14vw,11.5rem)]">
                COBAIN
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span {...lineAnim(2)} className="block text-[clamp(4.5rem,14vw,11.5rem)]">
                <span className="text-burnt">REPO</span><span className="text-outline-tan">RT</span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-7 max-w-xl text-sand text-base md:text-lg font-body font-light leading-relaxed"
            data-testid="hero-subtitle"
          >
            {subtitle.split("\n").map((line, i) => (
              <span key={i} className={i === 0 ? "block text-cream font-normal tracking-wide" : "block mt-2"}>
                {line}
              </span>
            ))}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={YOUTUBE_CHANNEL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-watch-latest-cta"
              className="group flex items-center gap-3 bg-burnt hover:bg-burnt-hover text-cream font-mono text-xs tracking-[0.2em] px-8 py-4 rounded-full transition-all duration-300 hover:shadow-[0_0_36px_rgba(200,90,23,0.45)]"
            >
              <Play size={15} className="fill-current transition-transform duration-300 group-hover:scale-125" />
              WATCH THE LATEST
            </a>
            <button
              onClick={() => scrollToId("music")}
              data-testid="hero-listen-now-cta"
              className="group flex items-center gap-3 border border-tan/40 hover:border-tan text-cream font-mono text-xs tracking-[0.2em] px-8 py-4 rounded-full transition-all duration-300 hover:bg-tan/10"
            >
              <Headphones size={15} className="text-tan transition-transform duration-300 group-hover:scale-125" />
              LISTEN NOW
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, rotate: -4 }}
          animate={{ opacity: 1, scale: 1, rotate: -2 }}
          transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          style={{ y: imgY }}
          className="hidden lg:block absolute right-6 xl:right-16 top-1/2 -translate-y-1/2 w-[36%] max-w-[500px]"
        >
          <div className="relative border border-tan/25 p-2 bg-wood shadow-[0_40px_80px_rgba(0,0,0,0.6)]" data-testid="hero-brand-image">
            <img src={BRAND_IMAGE} alt="The Cobain Report — official brand collage" className="w-full aspect-[4/5] object-cover object-center" />
            <div className="absolute -top-4 -right-4 bg-burnt text-cream font-mono text-[10px] tracking-[0.2em] px-4 py-2.5 rotate-3 shadow-lg" data-testid="hero-badge-reach">
              24M+ VIEWS / QUARTER
            </div>
            <div className="absolute -bottom-5 -left-5 w-16 h-16 rounded-full bg-gold flex items-center justify-center shadow-[0_0_40px_rgba(230,161,0,0.4)] -rotate-6">
              <Play size={22} className="fill-obsidian text-obsidian ml-1" />
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-24 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2"
        aria-hidden="true"
      >
        <span className="font-mono text-[9px] tracking-[0.35em] text-clay">SCROLL</span>
        <span className="w-px h-10 bg-gradient-to-b from-burnt to-transparent" />
      </motion.div>
    </section>
  );
}
